import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trash2 } from 'lucide-react';
import '../../component-styles/ItemForm.css';
import { useProductFormData } from './useProductFormData';
import API_BASE from '../../utils/api.js';
import { validateWebImageFile } from '../../utils/imageUpload.js';
import notify from '../../utils/notify';
import serviceDefaultImg from '../../assets/service-default.png';

export default function AddServiceForm({ editingItem, onClose, refreshItems, setUnsavedChanges }) {
  const DEFAULT_IMG = serviceDefaultImg;

  const isEdit = Boolean(editingItem);
  const {
    categoryOptions,
    addCategory,
    deleteCategory,
    addVendor,
    vendors,
    qbVendors,
    qbConnected,
    loadingQbVendors,
    ensureVendorFromQb,
    salesCodeOptions,
    addSalesCode,
  } = useProductFormData('service');

  const [form, setForm] = useState({
    name: '',
    salesCode: '',
    category: '',
    vendorId: '',
    vendor: '',
    price: '',
    sellingPrice: '',
    link: '',
    description: '',
    notes: '',
    imageUrl: '',
  });
  const [newCategory, setNewCategory] = useState('');
  const [newVendorName, setNewVendorName] = useState('');
  const [selectedQbVendorId, setSelectedQbVendorId] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const isAddingCategoryRef = useRef(false);
  const isAddingVendorRef = useRef(false);
  const [showAddCodeModal, setShowAddCodeModal] = useState(false);
  const [newCodeForm, setNewCodeForm] = useState({ name: '', code: '' });

  const effectiveSalesCodeOptions = useMemo(() => {
    const options = Array.isArray(salesCodeOptions) ? [...salesCodeOptions] : [];
    const currentCode = String(form.salesCode || '')
      .trim()
      .toUpperCase();
    if (!currentCode) return options;
    const exists = options.some((row) => row.salesCode === currentCode);
    if (exists) return options;
    return [{ name: 'Current', salesCode: currentCode }, ...options];
  }, [form.salesCode, salesCodeOptions]);

  /* --------------------------------------------------
     PREFILL WHEN EDITING
  -------------------------------------------------- */
  useEffect(() => {
    if (!editingItem) return;

    setForm({
      name: editingItem.name ?? '',
      salesCode: editingItem.salesCode ?? '',
      category: editingItem.category ?? '',
      vendorId: editingItem.vendorId ?? '',
      vendor: editingItem.vendorName ?? editingItem.vendor ?? '',
      price: editingItem.price ?? '',
      sellingPrice: editingItem.sellingPrice ?? '',
      link: editingItem.link ?? '',
      description: editingItem.description ?? '',
      notes: editingItem.notes ?? editingItem.internalNotes ?? '',
      imageUrl: editingItem.imageUrl ?? '',
    });
  }, [editingItem]);

  useEffect(() => {
    if (!editingItem?.vendorId || !vendors.length) return;
    const match = vendors.find((v) => v.vendorId === editingItem.vendorId);
    if (match?.quickbooksVendorId) {
      setSelectedQbVendorId(match.quickbooksVendorId);
    }
  }, [editingItem, vendors]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setUnsavedChanges?.(true);
  };

  const validateRequiredFields = () => {
    const nextErrors = {};
    const trimmedName = form.name.trim();
    const parsedPrice = Number(form.price);

    if (!trimmedName) nextErrors.name = 'Service name is required.';
    if (!String(form.price).trim()) {
      nextErrors.price = 'Trade price is required.';
    } else if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      nextErrors.price = 'Trade price must be greater than 0.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
    e.target.value = '';
  };

  const uploadImageFile = async (file) => {
    if (!file) return;

    const validation = validateWebImageFile(file);
    if (!validation.ok) {
      notify(validation.error);
      return;
    }

    try {
      setUploadingImage(true);
      setUnsavedChanges?.(true);
      const token = localStorage.getItem('idToken');

      const signRes = await fetch(
        `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(
          file.name
        )}&type=${encodeURIComponent(validation.mime)}&size=${encodeURIComponent(file.size)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (!signRes.ok) throw new Error('Failed to get upload URL');

      const sign = await signRes.json();
      const uploadUrl = sign.uploadUrl || sign.url || sign.presignedUrl;
      const fileUrl = sign.fileUrl || sign.publicUrl;
      if (!uploadUrl || !fileUrl) throw new Error('Invalid upload response');

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': validation.mime },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('S3 upload failed');

      setForm((prev) => ({ ...prev, imageUrl: fileUrl }));
    } catch (err) {
      console.error('Failed to upload service image', err);
      notify('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
  };

  const handleAddCategory = async () => {
    if (isAddingCategoryRef.current) return;
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    isAddingCategoryRef.current = true;
    try {
      await addCategory(trimmed);
      setForm((prev) => ({ ...prev, category: trimmed }));
      setNewCategory('');
    } finally {
      isAddingCategoryRef.current = false;
    }
  };

  const handleAddCategoryKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    await handleAddCategory();
  };

  const handleDeleteCategory = async () => {
    const name = form.category;
    if (!name || !categoryOptions.custom.includes(name)) return;
    if (!window.confirm(`Delete category "${name}"?`)) return;
    const ok = await deleteCategory(name);
    if (ok) setForm((prev) => ({ ...prev, category: '' }));
  };

  const handleSavedVendorChange = (e) => {
    const vendorId = e.target.value;
    setUnsavedChanges?.(true);

    if (!vendorId) {
      setForm((prev) => ({ ...prev, vendorId: '', vendor: '' }));
      return;
    }

    const selected = vendors.find((v) => v.vendorId === vendorId);
    if (!selected) return;

    setForm((prev) => ({
      ...prev,
      vendorId: selected.vendorId || '',
      vendor: selected.name || '',
    }));
    setSelectedQbVendorId(selected.quickbooksVendorId || '');
  };

  const handleAddVendor = async () => {
    if (isAddingVendorRef.current) return;
    const trimmed = newVendorName.trim();
    if (!trimmed) return;
    isAddingVendorRef.current = true;
    try {
      const created = await addVendor(trimmed);
      if (!created) {
        notify('Failed to create vendor');
        return;
      }
      if (created.__duplicate) {
        notify.warning(`Vendor "${created.name || trimmed}" already exists — using the existing vendor.`);
      }
      setForm((prev) => ({
        ...prev,
        vendorId: created.vendorId || '',
        vendor: created.name || trimmed,
      }));
      setSelectedQbVendorId(created.quickbooksVendorId || '');
      setNewVendorName('');
      setUnsavedChanges?.(true);
    } finally {
      isAddingVendorRef.current = false;
    }
  };

  const handleAddVendorKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    await handleAddVendor();
  };

  const handleVendorChange = async (e) => {
    const qbVendorId = e.target.value;
    setSelectedQbVendorId(qbVendorId);
    setUnsavedChanges?.(true);

    if (!qbVendorId) {
      setForm((prev) => ({ ...prev, vendorId: '', vendor: '' }));
      return;
    }

    const qbVendor = qbVendors.find((v) => v.id === qbVendorId);
    if (!qbVendor) return;

    const ensured = await ensureVendorFromQb(qbVendor);
    setForm((prev) => ({
      ...prev,
      vendorId: ensured?.vendorId || '',
      vendor: qbVendor.displayName || '',
    }));
  };

  const handleAddSalesCode = () => {
    setNewCodeForm({ name: '', code: '' });
    setShowAddCodeModal(true);
  };

  const handleSaveNewCode = async () => {
    const trimmedName = newCodeForm.name.trim();
    const trimmedCode = newCodeForm.code.trim().toUpperCase();
    if (!trimmedName || !trimmedCode) {
      notify('Sales Account name and Sales Code are required.');
      return;
    }
    try {
      const created = await addSalesCode({ name: trimmedName, salesCode: trimmedCode });
      if (!created?.salesCode) throw new Error('Invalid sales code response');
      setForm((prev) => ({ ...prev, salesCode: created.salesCode }));
      setUnsavedChanges?.(true);
      setShowAddCodeModal(false);
    } catch (err) {
      console.error('Failed to add sales code', err);
      notify(err.message || 'Failed to add sales code');
    }
  };

  const categoryPlaceholder = useMemo(() => {
    if (categoryOptions.custom.length || categoryOptions.preset.length) {
      return 'Select Category';
    }
    return 'No categories yet';
  }, [categoryOptions]);

  /* --------------------------------------------------
     SUBMIT
  -------------------------------------------------- */
  async function handleSubmit(e) {
    e.preventDefault();
    if (uploadingImage) return;
    if (!validateRequiredFields()) return;

    await addCategory(form.category);

    const payload = {
      ...form,
      name: form.name.trim(),
      price: Number(form.price),
      sellingPrice: form.sellingPrice !== '' ? Number(form.sellingPrice) : null,
      internalNotes: form.notes,
      type: 'service',
      userId: localStorage.getItem('userId') || undefined,
    };

    const url = isEdit
      ? `${API_BASE}/api/products/${editingItem.itemId}`
      : `${API_BASE}/api/products`;

    const method = isEdit ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let message = 'Failed to save service';
        try {
          const body = await res.json();
          message = body?.error || message;
        } catch {}
        throw new Error(message);
      }

      refreshItems?.();
      onClose();
    } catch (err) {
      console.error('Failed to save service', err);
      notify('Failed to save service');
    }
  }

  return (
    <>
    <form className="item-grid service-form" onSubmit={handleSubmit}>
      <div className="required-note full">Fields marked with * are required.</div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-name">
          Service Name <span className="required-mark">*</span>
        </label>
        <input
          id="service-name"
          name="name"
          placeholder="Service Name"
          value={form.name}
          onChange={handleInput}
          required
          aria-invalid={Boolean(errors.name)}
        />
        {errors.name && <div className="field-error">{errors.name}</div>}
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-category">
          Category
        </label>
        <div className="category-select-row">
          <select id="service-category" name="category" value={form.category} onChange={handleInput}>
            <option value="">{categoryPlaceholder}</option>
            {categoryOptions.preset.length > 0 && (
              <optgroup label="Presets">
                {categoryOptions.preset.map((c) => (
                  <option key={`preset-${c}`} value={c}>
                    {c}
                  </option>
                ))}
              </optgroup>
            )}
            {categoryOptions.custom.length > 0 && (
              <optgroup label="Your Categories">
                {categoryOptions.custom.map((c) => (
                  <option key={`custom-${c}`} value={c}>
                    {c}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {categoryOptions.custom.includes(form.category) && (
            <button
              type="button"
              className="delete-category-btn"
              onClick={handleDeleteCategory}
              title="Delete this category"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        <div className="new-category-row">
          <input
            name="newCategory"
            placeholder="Add new category..."
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={handleAddCategoryKeyDown}
          />
          <button type="button" className="inline-action" onClick={handleAddCategory}>
            Add
          </button>
        </div>
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-sales-code">
          Sales Code
        </label>
        <select
          id="service-sales-code"
          name="salesCode"
          value={form.salesCode}
          onChange={handleInput}
        >
          <option value="">Select Sales Code</option>
          {effectiveSalesCodeOptions.map((row) => (
            <option key={`${row.name}-${row.salesCode}`} value={row.salesCode}>
              {row.salesCode} - {row.name}
            </option>
          ))}
        </select>
        <button type="button" className="inline-action" onClick={handleAddSalesCode}>
          Add New Sales Code
        </button>
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-saved-vendor">
          Vendor
        </label>
        <select
          id="service-saved-vendor"
          name="savedVendorId"
          value={vendors.some((v) => v.vendorId === form.vendorId) ? form.vendorId : ''}
          onChange={handleSavedVendorChange}
        >
          <option value="">Select Vendor</option>
          {vendors.map((v) => (
            <option key={v.vendorId} value={v.vendorId}>
              {v.name || v.companyName || 'Vendor'}
            </option>
          ))}
        </select>
        <div className="new-category-row">
          <input
            name="newVendor"
            placeholder="Add new vendor"
            value={newVendorName}
            onChange={(e) => {
              setNewVendorName(e.target.value);
              setUnsavedChanges?.(true);
            }}
            onKeyDown={handleAddVendorKeyDown}
          />
          <button type="button" className="inline-action" onClick={handleAddVendor}>
            Add
          </button>
        </div>
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-qb-vendor">
          QuickBooks Vendor
        </label>
        <select
          id="service-qb-vendor"
          name="vendorId"
          value={selectedQbVendorId}
          onChange={handleVendorChange}
          disabled={!qbConnected || loadingQbVendors}
        >
          <option value="">
            {qbConnected
              ? loadingQbVendors
                ? 'Loading vendors...'
                : 'Select QuickBooks Vendor'
              : 'Connect QuickBooks to load vendors'}
          </option>
          {qbVendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-price">
          Trade Price <span className="required-mark">*</span>
        </label>
        <input
          id="service-price"
          name="price"
          type="number"
          min="0.01"
          step="0.01"
          inputMode="decimal"
          placeholder="0.00"
          value={form.price}
          onChange={handleInput}
          required
          aria-invalid={Boolean(errors.price)}
        />
        {errors.price && <div className="field-error">{errors.price}</div>}
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="service-selling-price">
          Selling Price
        </label>
        <input
          id="service-selling-price"
          name="sellingPrice"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="Trade price + project markup"
          value={form.sellingPrice}
          onChange={handleInput}
        />
      </div>

      <input
        name="link"
        className="full"
        placeholder="Service Link"
        value={form.link}
        onChange={handleInput}
      />

      <textarea
        className="full"
        name="description"
        placeholder="Client-Facing Description"
        value={form.description}
        onChange={handleInput}
      />

      <textarea
        className="full"
        name="notes"
        placeholder="Internal Notes"
        value={form.notes}
        onChange={handleInput}
      />

      <div
        className="image-upload"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragActive(false);
        }}
        onDrop={handleImageDrop}
        style={isDragActive ? { outline: '2px dashed #7ca65c', outlineOffset: 4 } : undefined}
      >
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml"
          onChange={handleFileChange}
          disabled={uploadingImage}
        />
        <input
          name="imageUrl"
          placeholder="or paste URL"
          value={form.imageUrl}
          onChange={handleInput}
        />
      </div>

      <div className="image-preview">
        <img src={form.imageUrl || DEFAULT_IMG} alt="Preview" />
      </div>

      <div className="item-actions">
        <button type="button" className="cancel" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={uploadingImage}>
          {uploadingImage ? 'Uploading Image...' : isEdit ? 'Save Changes' : 'Add Service'}
        </button>
      </div>
    </form>

    {showAddCodeModal && createPortal(
      <div className="item-mini-modal-overlay" onClick={() => setShowAddCodeModal(false)}>
        <div className="item-mini-modal" onClick={(e) => e.stopPropagation()}>
          <h3>Add Sales Code</h3>
          <form
            className="item-mini-modal-form"
            onSubmit={(e) => { e.preventDefault(); handleSaveNewCode(); }}
          >
            <label>Account Name</label>
            <input
              autoFocus
              value={newCodeForm.name}
              placeholder="e.g., Accessories"
              onChange={(e) => setNewCodeForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <label>Sales Code</label>
            <input
              value={newCodeForm.code}
              placeholder="e.g., ACC"
              onChange={(e) =>
                setNewCodeForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
              }
            />
            <div className="item-mini-modal-actions">
              <button type="button" className="cancel" onClick={() => setShowAddCodeModal(false)}>
                Cancel
              </button>
              <button type="submit" className="save">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}
